/**
 * Split.io Client Factory
 * -----------------------
 * Initializes and manages a singleton Split.io client instance.
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-12
 */

import { AppConfig } from "@/lib/config";
import { constants } from "@/lib/utils/constants";

/**
 * Holds the singleton Split.io service instance.
 *
 * The service is initialized only once and the same instance is returned
 * for all subsequent calls to `getSplitService()`.
 */
let splitServiceInstance = null;

/**
 * Returns the singleton Split.io service.
 *
 * The Split.io SDK is loaded and initialized lazily on the first call.
 * Subsequent calls return the already initialized service instance.
 *
 * @param accountId - The account/user identifier used by Split.io for
 *                    evaluating feature flag treatments.
 * @returns A promise resolving to the Split.io service instance.
 *
 * @example
 * const splitService = await getSplitService(accountId);
 * const isEnabled = await splitService.getSplitKey("my-feature-flag");
 */
export const getSplitService = async (accountId) => {
  // Return the existing service instance if Split.io has already been initialized.
  console.log(splitServiceInstance)
  if (splitServiceInstance) return splitServiceInstance;

  // Dynamically import Split.io to avoid loading the SDK until it is actually needed.
  const { SplitFactory } = await import("@splitsoftware/splitio");

  // Create the Split.io factory using the application's authorization key.
  const factory = SplitFactory({
    core: {
      authorizationKey: AppConfig.SPLIT_AUTHORIZATION_KEY || "",
    },
  });

  // Create a Split.io client for the specified account.
  const client = factory.client(accountId);

  /**
   * Wait for the Split.io SDK to become ready before exposing
   * the service to the rest of the application.
   *
   * The service should not be used until the SDK has successfully
   * completed its initialization.
   */
  await new Promise((resolve, reject) => {
    // SDK has successfully initialized and is ready to evaluate treatments.
    client.on(client.Event.SDK_READY, resolve);

    // SDK failed to become ready within the configured timeout.
    client.on(client.Event.SDK_READY_TIMED_OUT, () => {
      reject(new Error("Split.io SDK initialization timed out"));
    });

    // SDK initialization itself failed.
    client.on(client.Event.SDK_INIT_TIMED_OUT, () => {
      reject(new Error("Split.io SDK init failed"));
    });
  });

  /**
   * Expose only the Split.io functionality required by the application.
   *
   * Keeping the SDK client private prevents other parts of the application
   * from directly interacting with the Split.io client.
   */
  splitServiceInstance = {
    /**
     * Evaluates a Split.io feature flag and returns whether it is enabled.
     *
     * @param key - The Split.io feature flag key.
     * @returns `true` when the treatment is considered enabled; otherwise `false`.
     */
    getSplitKey: async (key) => {
      // Get the treatment assigned to the current account.
      const treatment = await client.getTreatment(key);

      /**
       * Temporary staging/UAT behavior for the `isUIDAccount` flag.
       *
       * This flag intentionally uses the inverse of the normal Split.io
       * treatment logic: anything other than "on" is treated as enabled.
       *
       * TODO: Remove this special handling once the staging/UAT requirement
       * is no longer needed.
       */
      if (key === constants.SPLIT_IO_KEYS.isUIDAccount) {
        return treatment !== "on";
      }

      // For all other feature flags, only the "on" treatment is considered enabled.
      return treatment === "on";
    },
  };

  // Return the newly initialized singleton service instance.
  return splitServiceInstance;
};
